/**
 * PropertyAmenities Component
 * 
 * Core component for displaying property amenities
 */

'use client';

import type { PropertyAmenity } from '@/lib/api/clients/properties';
import { Check } from '@phosphor-icons/react/dist/ssr';

export interface PropertyAmenitiesProps {
  amenities: PropertyAmenity[];
  messages: {
    amenities: {
      title: string;
      subtitle: string;
      noAmenities: string;
    };
  };
  className?: string;
}

export function PropertyAmenities({
  amenities,
  messages,
  className = ''
}: PropertyAmenitiesProps) {
  if (!amenities || amenities.length === 0) {
    return (
      <div className={`font-inter ${className}`}>
        <h2 className="text-xl md:text-2xl font-semibold text-[#222222] mb-2 font-inter">
          {messages.amenities.title}
        </h2>
        <p className="text-[#717171] mb-4">{messages.amenities.subtitle}</p>
        <p className="text-[#717171] italic">{messages.amenities.noAmenities}</p>
      </div>
    );
  }

  return (
    <div className={`font-inter ${className}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-1 h-8 bg-gradient-to-b from-[#BBA339] to-[#E4CE4A] rounded-full"></div>
        <h2 className="text-xl md:text-2xl font-bold text-[#222222] font-inter">
          {messages.amenities.title}
        </h2>
      </div>
      <p className="text-[#717171] mb-6 ml-7">{messages.amenities.subtitle}</p>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {amenities.map((amenity) => (
          <div 
            key={amenity.id} 
            className="group flex items-start gap-4 p-4 rounded-xl hover:bg-gradient-to-r hover:from-[#BBA339]/5 hover:to-transparent transition-all duration-200 border border-transparent hover:border-[#BBA339]/20"
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#BBA339]/20 to-[#E4CE4A]/10 flex items-center justify-center mt-0.5 group-hover:from-[#BBA339]/30 group-hover:to-[#E4CE4A]/20 transition-all">
              <Check size={18} weight="bold" className="text-[#BBA339]" />
            </div>
            <div className="flex-1">
              <span className="text-[#222222] font-semibold text-base">{amenity.amenity.name}</span>
              {amenity.amenity.description && (
                <p className="text-sm text-[#717171] mt-1">{amenity.amenity.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
